// Drill-down list for the real Orders tab: the exact rows behind a clicked number (or search
// matches). A row opens that order's step-by-step path on this page.
import { Alert, Drawer, Segmented, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import { getOrdersList, getPosList, getShipmentsList } from "../../api/orders";
import { fmtWeight, fmtWhen } from "../../format";
import { OcListSection, OcOrdersList, OcRow, OcScopeParams } from "../../types/orders";
import styles from "../../index.module.scss";

export type OrdersListRequest =
  | { mode: "metric"; title: string; metric: string; scope: OcScopeParams; tab?: "orders" | "pos" | "shipments" }
  | { mode: "rows"; title: string; rows: OcRow[] };

const KIND_LABEL: Record<OcRow["kind"], string> = {
  order: "Order",
  quote: "Quote",
  po: "PO",
  leg: "Leg",
  shipment: "Shipment",
  reattempt: "Re-attempt",
  cart: "Cart line",
};


const columns: ColumnsType<OcRow> = [
  {
    title: "ID",
    dataIndex: "id",
    width: 150,
    fixed: "left",
    render: (v: string, r) => (
      <div>
        {/* A shipment shows the POs it carries, one per line (as on the Control Tower cards). */}
        {r.po_numbers?.length ? (
          r.po_numbers.map((po) => (
            <div key={po} className={styles.mono}>
              {po}
            </div>
          ))
        ) : (
          <div className={styles.mono}>{v}</div>
        )}
        {r.kind !== "order" && r.order_id && r.order_id !== v ? <div className={styles.mutedSmall}>{r.order_id}</div> : null}
        {(r.tags ?? []).map((t) => (
          <Tag key={t} color={TAG_COLOR[t] ?? "default"} className={styles.escTag}>
            {t}
          </Tag>
        ))}
      </div>
    ),
  },
  { title: "Type", dataIndex: "kind", width: 90, render: (v: OcRow["kind"]) => <Tag className={styles.escTag}>{KIND_LABEL[v]}</Tag> },
  { title: "Client", dataIndex: "client", width: 180, ellipsis: true },
  {
    title: "Route",
    key: "route",
    width: 190,
    render: (_, r) =>
      r.origin || r.destination ? (
        <span>
          {r.origin ?? "–"} <span className={styles.muted}>→</span> {r.destination ?? "–"}
        </span>
      ) : (
        <span className={styles.muted}>–</span>
      ),
  },
  { title: "Delivery", dataIndex: "delivery_type", width: 100, render: (v: string | null) => v ?? <span className={styles.muted}>–</span> },
  { title: "Vendor / carrier", dataIndex: "party", width: 200, ellipsis: true },
  { title: "Weight", dataIndex: "weight_kg", width: 80, align: "right", render: (v: number | null) => fmtWeight(v) },
  { title: "State", dataIndex: "state", width: 220, ellipsis: true },
  { title: "Date", dataIndex: "date", width: 110, render: (v: string | null) => fmtWhen(v) },
];

// Row tags: escalation raised on this PO / shipment (open red, resolved grey), PO re-raised after a
// deallocation, PO reached Zoho after 6 PM.
const TAG_COLOR: Record<string, string> = {
  Escalated: "red",
  "Escalated · resolved": "default",
  "Re-raised": "purple",
  "PO after 6 PM": "orange",
};

// The escalation or tech error behind an escalation / tech-error list, shown only on those lists. SLA
// lists show "<status now> | <delayed by>"; the expected time and how late it is come on hover.
const DETAIL_COLUMN: ColumnsType<OcRow>[number] = {
  title: "Detail",
  dataIndex: "note",
  width: 340,
  render: (v: string | null | undefined, r) =>
    !v ? (
      <span className={styles.muted}>–</span>
    ) : r.note_hint ? (
      <Tooltip title={r.note_hint}>
        <span className={styles.hintText}>{v}</span>
      </Tooltip>
    ) : (
      <span title={v}>{v}</span>
    ),
};

const OrdersListDrawer: React.FC<{
  request: OrdersListRequest | null;
  onClose: () => void;
  onOpenPath: (r: OcRow) => void;
}> = ({ request, onClose, onOpenPath }) => {
  const [data, setData] = useState<OcOrdersList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Delivery breached / at-risk lists come split by where the shipment is; a section is its own list.
  const [section, setSection] = useState<string | null>(null);
  const [sections, setSections] = useState<OcListSection[] | null>(null);

  useEffect(() => {
    setSection(null);
    setSections(null);
  }, [request]);

  useEffect(() => {
    setData(null);
    setError(null);
    if (!request || request.mode !== "metric") return;
    let alive = true;
    setLoading(true);
    (request.tab === "pos" ? getPosList : request.tab === "shipments" ? getShipmentsList : getOrdersList)(
      request.scope,
      section ?? request.metric,
    )
      .then((res) => {
        if (!alive) return;
        setData(res);
        if (res.sections) setSections(res.sections);
      })
      .catch((e) => alive && setError(e?.response?.data?.detail ?? e?.message ?? "Could not load the list"))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [request, section]);

  const rows = request?.mode === "rows" ? request.rows : data?.rows ?? [];
  const total = request?.mode === "rows" ? request.rows.length : data?.total;

  return (
    <Drawer
      open={!!request}
      onClose={onClose}
      width="min(1300px, 96vw)"
      destroyOnClose
      title={
        <span>
          {request?.title}
          {total != null ? <span className={styles.drawerCount}>{total}</span> : null}
        </span>
      }
    >
      {error ? <Alert type="error" showIcon message={error} className={styles.inlineAlert} /> : null}
      {sections && sections.length > 1 ? (
        <Segmented
          className={styles.inlineAlert}
          value={section ?? sections[0].metric}
          onChange={(v) => setSection(String(v))}
          options={sections.map((s) => ({ value: s.metric, label: `${s.label} (${s.count})` }))}
        />
      ) : null}
      {data?.truncated ? (
        <Alert type="info" showIcon message={`Showing the first ${data.rows.length} of ${data.total}.`} className={styles.inlineAlert} />
      ) : null}
      <Table<OcRow>
        size="small"
        rowKey="ref"
        columns={rows.some((r) => r.note) ? [...columns.slice(0, 3), DETAIL_COLUMN, ...columns.slice(3)] : columns}
        dataSource={rows}
        loading={loading}
        pagination={{ pageSize: 50, hideOnSinglePage: true, showSizeChanger: false }}
        className={styles.denseTable}
        scroll={{ x: 1300 }}
        locale={{ emptyText: loading ? " " : "Nothing here." }}
        onRow={(r) => ({ onClick: () => r.sales_order_id && onOpenPath(r), className: r.sales_order_id ? styles.clickRow : undefined })}
      />
    </Drawer>
  );
};

export default OrdersListDrawer;
